import React, { useState } from "react";
import { TimeInput } from "@mantine/dates";
import {
  Card,
  Flex,
  Text,
  ActionIcon,
  Group,
  Button,
  Select,
} from "@mantine/core";
import dayjs from "dayjs";
import { IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { getUuid } from "@util/getUuid";

export const intervalValues = {
  hourly: 1,
  daily: 2,
  weekly: 3,
  monthly: 4,
  yearly: 5,
  ever: 6,
  custom: 7,
};

export const intervalValueStrings = Object.keys(intervalValues).map((m) => m);

export const ValueItem = ({ day, start, end, onRemoveClick }) => {
  const { t } = useTranslation();
  return (
    <Group mb="md" align="center">
      <ActionIcon
        onClick={onRemoveClick}
        size="sm"
        radius="xl"
        style={{ marginTop: "0" }}
        variant="light"
        color="red"
      >
        <IconTrash />
      </ActionIcon>
      <Text>
        {t("common:day")} {day}: {start} - {end}
      </Text>
    </Group>
  );
};

const defaultFormValues = {
  start: "",
  end: "",
  day: "",
};

export const ValueAdd = ({ onAdd, dates }) => {
  const { t } = useTranslation();
  const daysBetween = dayjs(dates.end).diff(dates.begin, "day") + 1;
  const daysBetweenArray = Array.from({ length: daysBetween }, (v, k) => k + 1);
  const dayOptions = daysBetweenArray.map((m, i) => ({
    label: `${t("common:day")} ${m} (${new Date(
      dayjs(dates.begin).add(i, "day")
    ).toLocaleDateString()})`,
    value: `${m}`,
  }));

  const [formValues, setFormValues] = useState(defaultFormValues);

  return (
    <div>
      <Select
        label={t("common:day")}
        data={dayOptions}
        placeholder={t("common:select_one")}
        value={formValues.day}
        onChange={(e) => setFormValues({ ...formValues, day: e })}
      />
      <Group grow>
        <TimeInput
          label={t("common:start")}
          format="12"
          value={formValues.start}
          onChange={(e) => {
            setFormValues({ ...formValues, start: e.target.value });
          }}
        />
        <TimeInput
          label={t("common:end")}
          format="12"
          value={formValues.end}
          onChange={(e) =>
            setFormValues({ ...formValues, end: e.target.value })
          }
        />
      </Group>
      <Button
        fullWidth
        mt="md"
        onClick={() => {
          onAdd({ ...formValues, uuid: getUuid() });
          setFormValues(defaultFormValues);
        }}
        disabled={!formValues.end || !formValues.start || !formValues.day}
      >
        {t("common:add")}
      </Button>
    </div>
  );
};

export const IntervalItem = ({
  frequency = null,
  interval = null,
  values = [],
  onRemoveClick,
}) => {
  const { t } = useTranslation();
  return (
    <Card>
      <Flex
        align="center"
        gap="xs"
        mb={intervalValues.custom === interval ? "xs" : "none"}
      >
        <Text fw={500}>
          {interval !== intervalValues.custom && (
            <React.Fragment>
              {frequency === -1 ? t("common:unlimited") : frequency}{" "}
              {frequency === 1
                ? t("common:time_singular")
                : t("common:time_plural")}
            </React.Fragment>
          )}
          {""}{" "}
          {t(
            `common:${intervalValueStrings[interval - 1]?.toLocaleLowerCase()}`
          )}
        </Text>
        {onRemoveClick !== undefined && (
          <ActionIcon
            onClick={onRemoveClick}
            size="sm"
            radius="xl"
            style={{ marginTop: "0" }}
            variant="light"
            color="red"
            title="Remove"
          >
            <IconTrash />
          </ActionIcon>
        )}
      </Flex>
      {interval === intervalValues.custom && (
        <React.Fragment>
          {values.map((v) => (
            <Text size="sm" c="dimmed" key={v.uuid}>
              {t("common:day")} {v.day}: {v.start} - {v.end}
            </Text>
          ))}
        </React.Fragment>
      )}
    </Card>
  );
};
